import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '../layouts/AdminLayout';
import { useAuth } from '../context/AuthContext';
import { useChatbot } from '../context/ChatbotContext';
import {
  Settings,
  Save,
  AlertCircle,
  CheckCircle,
  Loader2,
  User,
  MessageSquare,
} from 'lucide-react';

const SettingSection = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
    <div className="px-6 py-4 border-b border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
        <Icon className="w-5 h-5 mr-2 text-orange-500" />
        {title}
      </h3>
    </div>
    <div className="p-6">
      {children}
    </div>
  </div>
);

const ToggleSwitch = ({ label, checked, onChange, description }) => (
  <div className="flex items-start justify-between py-4">
    <div className="flex-1">
      <label className="text-sm font-medium text-gray-900">{label}</label>
      {description && (
        <p className="text-sm text-gray-500 mt-1">{description}</p>
      )}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={`${
        checked ? 'bg-orange-500' : 'bg-gray-200'
      } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2`}
      onClick={() => onChange(!checked)}
    >
      <span
        className={`${
          checked ? 'translate-x-5' : 'translate-x-0'
        } pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
      />
    </button>
  </div>
);

const AdminSettings = () => {
  const { currentUser } = useAuth();
  const { isAdminChatbotEnabled, toggleAdminChatbot } = useChatbot();
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus(null);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSaveStatus({
        type: 'success',
        message: 'Settings saved successfully!'
      });
    } catch (error) {
      setSaveStatus({
        type: 'error',
        message: 'Failed to save settings. Please try again.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AdminLayout>
      <Helmet>
        <title>Admin | Settings</title>
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Admin Information */}
        <SettingSection title="Admin Information" icon={User}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Email</label>
              <p className="mt-1 text-sm text-gray-900">{currentUser?.email || 'Not available'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Name</label>
              <p className="mt-1 text-sm text-gray-900">{currentUser?.displayName || 'Not available'}</p>
            </div>
          </div>
        </SettingSection>

        {/* Chatbot Settings */}
        <SettingSection title="Chatbot Settings" icon={MessageSquare}>
          <div className="space-y-4">
            <ToggleSwitch
              label="Enable Admin Chatbot"
              checked={isAdminChatbotEnabled}
              onChange={toggleAdminChatbot}
              description="Enable the admin chatbot for automated assistance"
            />
          </div>
        </SettingSection>

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Saving...
              </>
            ) : (
              <>
                <Save className="-ml-1 mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </button>
        </div>

        {saveStatus && (
          <div className={`mt-4 p-4 rounded-md ${
            saveStatus.type === 'success' 
              ? 'bg-green-50 border-l-4 border-green-400' 
              : 'bg-red-50 border-l-4 border-red-400'
          }`}>
            <div className="flex">
              {saveStatus.type === 'success' ? (
                <CheckCircle className="h-5 w-5 text-green-400" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-400" />
              )}
              <div className="ml-3">
                <p className={`text-sm ${
                  saveStatus.type === 'success' ? 'text-green-700' : 'text-red-700'
                }`}>
                  {saveStatus.message}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSettings; 